<script>
    import { preventDefault } from "svelte/legacy";
    import { slide } from "svelte/transition"; // Native Svelte smooth slide transition
    import { user, workspacePlan } from "../user/user.state.svelte.js";
    import { logout } from "../user/service.svelte.js";
    import statisticService from "../dashboard/service.js";
    import { onMount, tick } from "svelte";
    import { format } from "svelte-i18n";
    import { params } from "@roxi/routify";

    let activeSubItem = $state({ id: "" });

    // Menu open/close states
    let assetsItemIsOpen = $state(false);
    let findingsItemIsOpen = $state(false);
    let vulsItemIsOpen = $state(false);
    let integrationsItemIsOpen = $state(false);
    let accountItemIsOpen = $state(false);

    let assetCount;
    let assetCountPromise = statisticService
        .countAssetByKind()
        .then((count) => (assetCount = count))
        .catch(() => false);

    const setNewAndOutdatedLinks = (age, elemType) => {
        let days_amount = age === "new" ? 7 : 30;
        let startDate = new Date();
        startDate.setDate(startDate.getDate() - days_amount);
        startDate = startDate.toISOString().split("T")[0];

        const setEndDate = (diff) => {
            let endDate = new Date();
            endDate.setDate(endDate.getDate() - diff);
            return endDate.toISOString().split("T")[0];
        };

        let today = new Date().toISOString().split("T")[0];

        const dates = {
            new: `first_discovery_at__gte=${startDate}T00:00:00.000-03:00&first_discovery_at__lte=${today}T23:59:59.999-03:00`,
            outdated: `last_discovery_at__lte=${setEndDate(30)}T23:59:59.999-03:00&last_discovery_at__gte=2021-01-28T00:00:00.000-03:00`,
        };

        if (elemType === "finding") {
            return `/finding?status__in=open,accepted&${dates[age]}`;
        } else if (elemType === "asset") {
            return `/asset?in_scope=true&${dates[age]}`;
        }
    };

    let routes = [
        {
            path: "/dashboard",
            label: $format("layout.sidebar.dashboard"),
            icon: "fas fa-chart-area",
            id: "menuDashboardBtn",
            class: "dashboard",
        },
        {
            path: "#",
            label: $format("layout.sidebar.vulnerabilities"),
            icon: "fas fa-skull-crossbones",
            id: "menuVulsBtn",
            class: "vulnerabilities",
        },
        {
            path: "#",
            label: $format("layout.sidebar.findings"),
            icon: "fas fa-radiation",
            id: "menuFindingsBtn",
            class: "findings",
        },
        {
            path: "#",
            label: $format("layout.sidebar.assets"),
            icon: "fas fa-archive",
            id: "menuAssetsBtn",
            class: "assets",
        },
        {
            path: "/certificate?asset__kind__in=hostname",
            label: $format("layout.sidebar.certificates"),
            icon: "fas fa-lock",
            id: "menuCertificateBtn",
            class: "certificate",
        },
        {
            path: "/property/asset/cpe",
            label: $format("layout.sidebar.products"),
            icon: "fas fa-tools",
            id: "menuProductBtn",
            class: "product",
        },
        {
            path: "/tag",
            label: $format("layout.sidebar.tags"),
            icon: "fas fa-tags",
            id: "menuTagBtn",
            class: "tags",
        },
        {
            path: "/log",
            label: $format("layout.sidebar.log"),
            icon: "fas fa-align-justify",
            id: "menuLogsBtn",
            class: "log",
        },
        {
            path: "/snapshot",
            label: "Snapshots",
            icon: "fas fa-camera",
            id: "menuSnapshotBtn",
            class: "snapshot",
        },
        {
            path: "/report/list",
            label: $format("layout.sidebar.reports"),
            icon: "fas fa-file-contract",
            id: "menuReportBtn",
            class: "report",
        },
    ];

    if (user.data.is_internal) {
        routes.push({
            path: "/phishing/campaign",
            label: $format("layout.sidebar.phishing_campaign"),
            icon: "fas fa-fish",
            id: "menuPhishingCampaignBtn",
            class: "phishingCampaign",
        });
    }

    let accountSubItems = [
        {
            path: "/user",
            label: $format("layout.sidebar.users"),
            icon: "fas fa-users",
            id: "menuUserBtn",
            class: "users",
        },
        {
            path: "/billing",
            label: $format("layout.sidebar.billing"),
            icon: "fas fa-file-invoice-dollar",
            id: "menuBillingBtn",
            class: "billing",
        },
        {
            path: "/pentest",
            label: $format("layout.sidebar.pentest"),
            icon: "/img/icon-swords.png",
            id: "menuPenTestBtn",
            class: "pentest",
        },
        {
            path: "/phishing",
            label: $format("layout.sidebar.phishing"),
            icon: "fas fa-fish",
            id: "menuPhishingBtn",
            class: "phishing",
        },
        // --- NUEVO ITEM DE CONFIGURACIÓN DE SLA EN CUENTA ---
        {
            path: "/sla",
            label: $format("layout.sidebar.sla"),
            icon: "fas fa-stopwatch",
            id: "menuSlaConfigBtn",
            class: "slaConfig",
        },
        // ----------------------------------------------------
    ];

    if (workspacePlan.data !== "premium" && workspacePlan.data !== "aws") {
        accountSubItems.push({
            path: "/plan",
            label: $format("layout.sidebar.plan"),
            icon: "fas fa-rocket",
            id: "menuPlanBtn",
            class: "Plan",
        });
    }

    let subItems = [
        {
            path: "/asset?in_scope=true&finding_level__gte=high",
            label: $format("layout.sidebar.list"),
            icon: "fas fa-list",
            id: "menuAssetsListBtn",
            asset: "all",
            class: "allAssets",
            version: "beta",
        },
        {
            path: "/asset/domain",
            label: $format("asset.kind.domain.plural"),
            icon: "fas fa-globe",
            id: "menuDomainBtn",
            asset: "domain",
            class: "domain",
        },
        {
            path: "/asset/hostname",
            label: "Hostnames",
            icon: "fas fa-laptop-code",
            id: "menuHostnameBtn",
            asset: "hostname",
            class: "hostname",
        },
        {
            path: "/asset/public_network",
            label: $format("asset.kind.public_network.plural"),
            icon: "fas fa-network-wired",
            id: "menuPubNetworkBtn",
            class: "public_network",
        },
        {
            path: "/asset/public_ip",
            label: $format("asset.kind.public_ip.plural"),
            icon: "fas fa-laptop",
            id: "menuPubIpBtn",
            asset: "public_ip",
            class: "public_ip",
        },
        {
            path: "/asset/private_network",
            label: $format("asset.kind.private_network.plural"),
            icon: "fas fa-ethernet",
            id: "menuPrivNetworkBtn",
            class: "network",
        },
        {
            path: "/asset/private_ip",
            label: $format("asset.kind.private_ip.plural"),
            icon: "fas fa-laptop-house",
            id: "menuPrivateIpBtn",
            class: "ip",
        },
        {
            path: "/asset/email",
            label: "Emails",
            icon: "fas fa-envelope",
            id: "menuEmailBtn",
            class: "email",
        },
        {
            path: "/asset/ad",
            label: $format("asset.kind.ad.singular"),
            icon: "fas fa-address-book",
            id: "menuAdBtn",
            class: "ad",
        },
        {
            path: "/asset/cloud",
            label: $format("asset.kind.cloud.plural"),
            icon: "fas fa-cloud",
            id: "menuCloudBtn",
            class: "cloud",
        },
        {
            path: "/asset/software",
            label: $format("asset.kind.software.plural"),
            icon: "fas fa-code-branch",
            id: "menuSoftwareBtn",
            class: "software",
        },
        {
            path: "/asset/endpoint",
            label: $format("asset.kind.endpoint.plural"),
            icon: "fas fa-project-diagram",
            id: "menuEndpointBtn",
            class: "endpoint",
        },
        {
            path: "/property/asset",
            label: $format("layout.sidebar.props"),
            icon: "fas fa-shapes",
            id: "menuApropsBtn",
            class: "props",
        },
        {
            path: setNewAndOutdatedLinks("new", "asset"),
            label: $format("layout.sidebar.new_assets"),
            icon: "far fa-newspaper",
            id: "newAssets",
            class: "newAssets",
        },
        {
            path: setNewAndOutdatedLinks("outdated", "asset"),
            label: $format("layout.sidebar.outdated_assets"),
            icon: "fas fa-clock",
            id: "outdatedAssets",
            class: "outdatedAssets",
        },
    ];

    let findingsSubItems = [
        {
            path: "/finding?status__in=open,accepted&vulnerability__level__in=critical,high",
            label: $format("layout.sidebar.list"),
            icon: "fas fa-list",
            id: "menuFindingsListBtn",
            class: "findingsMine",
            type: "base",
        },
        {
            path: "/finding?vulnerability__id__ilike=LEAK-",
            label: $format("layout.sidebar.leaks"),
            icon: "fas fa-faucet",
            id: "menuLeakBtn",
            class: "findingsMine",
            type: "leak",
        },
        {
            path: "/property/finding?finding__status__in=open,accepted",
            label: $format("layout.sidebar.props"),
            icon: "fas fa-shapes",
            id: "menuFpropsBtn",
            class: "propsMenuLink",
            type: "leak",
        },
        {
            path: setNewAndOutdatedLinks("new", "finding"),
            label: $format("layout.sidebar.new_findings"),
            icon: "far fa-newspaper",
            id: "menuNewFindings",
            class: "propsMenuLink",
            type: "leak",
        },
        {
            path: setNewAndOutdatedLinks("outdated", "finding"),
            label: $format("layout.sidebar.outdated_findings"),
            icon: "fas fa-clock",
            id: "menuOutdatedFindings",
        },
        // --- DEVUELTO EL ITEM DE SLA VENCIDO A HALLAZGOS ---
        {
            path: "/finding?status=open&sla_days_remaining__lte=0",
            label: $format("layout.sidebar.findings_sla"),
            icon: "fas fa-stopwatch",
            id: "menuSlaExpiredBtn",
            class: "slaExpired",
            type: "base",
        },
        // ---------------------------------------------------
    ];

    let vulsSubItems = [
        {
            path: "/vulnerability?level__in=critical,high",
            label: $format("layout.sidebar.list"),
            icon: "fas fa-list",
            id: "menuVulnerabilityBtn",
            class: "vulnerability",
        },
        {
            path: "/property/vulnerability",
            label: $format("layout.sidebar.props"),
            icon: "fas fa-shapes",
            id: "menuVulnerabilityPropsBtn",
            class: "vulnerability",
        },
    ];

    let integrationsSubItems = [
        {
            path: "/integration",
            label: $format("layout.sidebar.list"),
            icon: "fas fa-list",
            id: "menuIntegrationsListBtn",
            class: "integrations",
        },
    ];

    if (user.data.is_internal) {
        vulsSubItems.push({
            path: "/vulnerability/custom",
            label: $format("layout.sidebar.custom"),
            icon: "fas fa-user-secret",
            id: "menuCustomBtn",
            class: "custom",
        });
        vulsSubItems.push({
            path: "/vulnerability/custom/logbook",
            label: $format("layout.sidebar.logbook"),
            icon: "fas fa-book-dead",
            id: "menuLogbookBtn",
            class: "logbook",
        });
        integrationsSubItems.push({
            path: "/integration/agent",
            label: "Vulseek Agent",
            icon: "fas fa-user-shield",
            id: "menuAgentBtn",
            class: "agent",
        });
    }

    integrationsSubItems.push({
        path: "/integration/scanner",
        label: "Vulseek Scanner",
        icon: "fas fa-crosshairs",
        id: "menuScannerBtn",
        class: "scanner",
    });

    routes.push({
        path: "#",
        label: $format("layout.sidebar.integrations"),
        icon: "fas fa-sitemap",
        id: "menuIntegrationsBtn",
        class: "integrations",
    });

    let mail = user.data.email ? user.data.email.split("@") : "";
    let logoSrc = $state("/img/vulseek-logo_dark.png");

    const handleLogout = () => {
        logout(false, "/login");
    };

    const logoOnMouseOver = () => {
        logoSrc = "/img/vulseek-logo.png";
    };

    const logoOnMouseLeave = () => {
        logoSrc = "/img/vulseek-logo_dark.png";
    };

    const getRouteUrl = (target) => {
        return target || "#";
    };

    // Framework-Agnostic Reactive URL Tracking
    let currentPath = $state("");
    let currentQuery = $state({});

    const updatePath = () => {
        if (typeof window !== "undefined") {
            currentPath = window.location.pathname;
            currentQuery = Object.fromEntries(
                new URLSearchParams(window.location.search),
            );
        }
    };

    $effect(() => {
        const _p = $params;
        updatePath();
    });

    // Active State Helpers
    const assetChildIsActive = () => {
        if (!currentPath) return false;
        return (
            currentPath.startsWith("/asset") ||
            currentPath.startsWith("/property/asset")
        );
    };

    const accountChildIsActive = () => {
        if (!currentPath) return false;
        return (
            currentPath.startsWith("/user") ||
            currentPath.startsWith("/billing") ||
            currentPath.startsWith("/plan") ||
            currentPath.startsWith("/phishing") ||
            currentPath.startsWith("/pentest") ||
            currentPath.startsWith("/sla") // Agregado aquí para que mantenga activo el menú de Cuenta
        );
    };

    const findingChildIsActive = () => {
        if (!currentPath) return false;
        return currentPath.startsWith("/finding"); // Restaurado a su comportamiento base original
    };

    const vulChildIsActive = () => {
        return currentPath ? currentPath.startsWith("/vulnerability") : false;
    };

    const isRouteActive = (routePath) => {
        if (!currentPath || routePath === "#") return false;
        return (
            currentPath === routePath || currentPath.startsWith(routePath + "/")
        );
    };

    const isSubItemActive = (subItemPath) => {
        if (!subItemPath || subItemPath === "#" || !currentPath) return false;

        const [path, query] = subItemPath.split("?");
        const cleanCurrent =
            currentPath.length > 1
                ? currentPath.replace(/\/$/, "")
                : currentPath;
        const cleanTarget = path.length > 1 ? path.replace(/\/$/, "") : path;

        if (cleanCurrent !== cleanTarget) return false;

        if (query) {
            const queryParams = new URLSearchParams(query);
            for (const [key, value] of queryParams.entries()) {
                if (currentQuery[key] !== value) return false;
            }
        }
        return true;
    };

    onMount(async () => {
        await tick();
        updatePath();

        window.addEventListener("popstate", updatePath);

        const originalPushStateNative =
            history.pushStateNative || history.pushState;
        if (history.pushStateNative) {
            history.pushStateNative = function (...args) {
                originalPushStateNative.apply(this, args);
                updatePath();
            };
        }

        const originalReplaceStateNative =
            history.replaceStateNative || history.replaceState;
        if (history.replaceStateNative) {
            history.replaceStateNative = function (...args) {
                originalReplaceStateNative.apply(this, args);
                updatePath();
            };
        }

        const originalPushState = history.pushState;
        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            updatePath();
        };

        const originalReplaceState = history.replaceState;
        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            updatePath();
        };

        return () => {
            window.removeEventListener("popstate", updatePath);
            if (history.pushStateNative)
                history.pushStateNative = originalPushStateNative;
            if (history.replaceStateNative)
                history.replaceStateNative = originalReplaceStateNative;
            history.pushState = originalPushState;
            history.replaceState = originalReplaceState;
        };
    });
</script>

<aside class="app-sidebar bg-white elevation-4">
    <!-- Brand Logo -->
    <a
        href="/"
        class="brand-link d-flex align-items-center ps-3"
        onmouseover={logoOnMouseOver}
        onfocus={logoOnMouseOver}
        onmouseleave={logoOnMouseLeave}
    >
        <img src={logoSrc} alt="Logo" class="brand-image logo-img" />
        <span class="brand-text d-flex flex-column ms-2">
            <span class="fw-bold brand-title">Vulseek</span>
            <small class="text-muted brand-subtitle">By Securetia</small>
        </span>
    </a>

    <!-- Sidebar Wrapper -->
    <div class="sidebar-wrapper">
        <nav class="mt-2">
            <ul class="nav nav-pills sidebar-menu flex-column" role="menu">
                <!-- User Profile Item -->
                <li class="nav-item">
                    <a
                        href={getRouteUrl("/me")}
                        class="nav-link t-menu-me"
                        id="UserMeBtn"
                        class:active={isRouteActive("/me")}
                    >
                        <i class="nav-icon fas fa-user"></i>
                        <p title={user.data.email}>{mail[0]}</p>
                    </a>
                </li>

                <!-- Account Parent Dropdown -->
                <li class="nav-item" class:menu-open={accountItemIsOpen}>
                    <a
                        id="menuAccount"
                        href="#"
                        class="nav-link t-menu-account"
                        class:active={accountChildIsActive()}
                        onclick={preventDefault(
                            () => (accountItemIsOpen = !accountItemIsOpen),
                        )}
                    >
                        <i class="nav-icon fas fa-building"></i>
                        <p>
                            {$format("layout.sidebar.account")}
                            <i class="nav-arrow fas fa-chevron-right"></i>
                        </p>
                    </a>

                    {#if accountItemIsOpen}
                        <ul
                            class="nav nav-treeview"
                            transition:slide={{ duration: 250 }}
                        >
                            {#each accountSubItems as subItem}
                                <li class="nav-item">
                                    <a
                                        id={subItem.id}
                                        href={getRouteUrl(subItem.path)}
                                        class="nav-link t-menu-{subItem.class}"
                                        class:active={isSubItemActive(
                                            subItem.path,
                                        )}
                                    >
                                        {#if subItem.path !== "/pentest"}
                                            <i class="nav-icon {subItem.icon}"
                                            ></i>
                                        {:else}
                                            <img
                                                class="pentest-icon nav-icon"
                                                src={isSubItemActive(
                                                    subItem.path,
                                                )
                                                    ? "/img/icon-swords-white.png"
                                                    : subItem.icon}
                                                alt=""
                                            />
                                        {/if}
                                        <p>{subItem.label}</p>
                                    </a>
                                </li>
                            {/each}
                        </ul>
                    {/if}
                </li>

                <!-- Dynamic Routes Loop -->
                {#each routes as route}
                    {#if route.path !== "#"}
                        <li class="nav-item">
                            <a
                                id={route.id}
                                href={getRouteUrl(route.path)}
                                class="nav-link"
                                class:active={isRouteActive(route.path)}
                            >
                                <i class="nav-icon {route.icon}"></i>
                                <p>
                                    {route.label}
                                    {#await assetCountPromise then assetCount}
                                        {#if route.asset && assetCount[route.asset]}
                                            <i
                                                class="ms-2 fas fa-exclamation-circle text-warning"
                                                title={"No se han cargado " +
                                                    route.label.toLowerCase()}
                                            ></i>
                                        {/if}
                                    {/await}
                                </p>
                            </a>
                        </li>
                    {:else if route.id === "menuAssetsBtn"}
                        <!-- Assets Parent Dropdown -->
                        <li class="nav-item" class:menu-open={assetsItemIsOpen}>
                            <a
                                id="menuAssets"
                                href="#"
                                class="nav-link t-menu-assets"
                                class:active={assetChildIsActive()}
                                onclick={preventDefault(
                                    () =>
                                        (assetsItemIsOpen = !assetsItemIsOpen),
                                )}
                            >
                                <i class="nav-icon {route.icon}"></i>
                                <p>
                                    {route.label}
                                    <i class="nav-arrow fas fa-chevron-right"
                                    ></i>
                                </p>
                            </a>

                            {#if assetsItemIsOpen}
                                <ul
                                    class="nav nav-treeview"
                                    transition:slide={{ duration: 250 }}
                                >
                                    {#each subItems as subItem}
                                        <li class="nav-item">
                                            <a
                                                id={subItem.id}
                                                href={getRouteUrl(subItem.path)}
                                                class="nav-link t-menu-{subItem.class}"
                                                class:active={isSubItemActive(
                                                    subItem.path,
                                                )}
                                            >
                                                <i
                                                    class="nav-icon {subItem.icon}"
                                                ></i>
                                                <p>
                                                    {subItem.label}
                                                    {#await assetCountPromise then assetCount}
                                                        {#if subItem.asset && !assetCount[subItem.asset] && subItem.class !== "allAssets"}
                                                            <i
                                                                class="ms-2 fas fa-exclamation-circle text-warning"
                                                                title={"No se han cargado " +
                                                                    subItem.label.toLowerCase()}
                                                            ></i>
                                                        {/if}
                                                    {/await}
                                                </p>
                                            </a>
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        </li>
                    {:else if route.id === "menuFindingsBtn"}
                        <!-- Findings Parent Dropdown -->
                        <li
                            class="nav-item"
                            class:menu-open={findingsItemIsOpen}
                        >
                            <a
                                id="menuFindings"
                                href="#"
                                class="nav-link t-menu-findings"
                                class:active={findingChildIsActive()}
                                onclick={preventDefault(
                                    () =>
                                        (findingsItemIsOpen =
                                            !findingsItemIsOpen),
                                )}
                            >
                                <i class="nav-icon {route.icon}"></i>
                                <p>
                                    {route.label}
                                    <i class="nav-arrow fas fa-chevron-right"
                                    ></i>
                                </p>
                            </a>

                            {#if findingsItemIsOpen}
                                <ul
                                    class="nav nav-treeview"
                                    transition:slide={{ duration: 250 }}
                                >
                                    {#each findingsSubItems as subItem}
                                        <li class="nav-item">
                                            <a
                                                id={subItem.id}
                                                href={getRouteUrl(subItem.path)}
                                                class="nav-link t-menu-{subItem.class}"
                                                class:active={isSubItemActive(
                                                    subItem.path,
                                                )}
                                            >
                                                <i
                                                    class="nav-icon {subItem.icon}"
                                                ></i>
                                                <p>{subItem.label}</p>
                                            </a>
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        </li>
                    {:else if route.id === "menuIntegrationsBtn"}
                        <!-- Integrations Parent Dropdown -->
                        <li
                            class="nav-item"
                            class:menu-open={integrationsItemIsOpen}
                        >
                            <a
                                id="menuIntegrations"
                                href="#"
                                class="nav-link t-menu-integrations"
                                class:active={isRouteActive("/integration")}
                                onclick={preventDefault(
                                    () =>
                                        (integrationsItemIsOpen =
                                            !integrationsItemIsOpen),
                                )}
                            >
                                <i class="nav-icon {route.icon}"></i>
                                <p>
                                    {route.label}
                                    <i class="nav-arrow fas fa-chevron-right"
                                    ></i>
                                </p>
                            </a>

                            {#if integrationsItemIsOpen}
                                <ul
                                    class="nav nav-treeview"
                                    transition:slide={{ duration: 250 }}
                                >
                                    {#each integrationsSubItems as subItem}
                                        <li class="nav-item">
                                            <a
                                                id={subItem.id}
                                                href={getRouteUrl(subItem.path)}
                                                class="nav-link t-menu-{subItem.class}"
                                                class:active={isSubItemActive(
                                                    subItem.path,
                                                )}
                                            >
                                                <i
                                                    class="nav-icon {subItem.icon}"
                                                ></i>
                                                <p>{subItem.label}</p>
                                            </a>
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        </li>
                    {:else}
                        <!-- Vulnerabilities Parent Dropdown -->
                        <li class="nav-item" class:menu-open={vulsItemIsOpen}>
                            <a
                                id="menuVuls"
                                href="#"
                                class="nav-link t-menu-vuls"
                                class:active={vulChildIsActive()}
                                onclick={preventDefault(
                                    () => (vulsItemIsOpen = !vulsItemIsOpen),
                                )}
                            >
                                <i class="nav-icon {route.icon}"></i>
                                <p>
                                    {route.label}
                                    <i class="nav-arrow fas fa-chevron-right"
                                    ></i>
                                </p>
                            </a>

                            {#if vulsItemIsOpen}
                                <ul
                                    class="nav nav-treeview"
                                    transition:slide={{ duration: 250 }}
                                >
                                    {#each vulsSubItems as subItem}
                                        <li class="nav-item">
                                            <a
                                                id={subItem.id}
                                                href={getRouteUrl(subItem.path)}
                                                class="nav-link t-menu-{subItem.class}"
                                                class:active={isSubItemActive(
                                                    subItem.path,
                                                )}
                                            >
                                                <i
                                                    class="nav-icon {subItem.icon}"
                                                ></i>
                                                <p>{subItem.label}</p>
                                            </a>
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        </li>
                    {/if}
                {/each}

                <!-- Logout Link -->
                <li class="nav-item">
                    <a
                        id="logoutBtn"
                        href="#"
                        class="nav-link t-menu-logout"
                        onclick={preventDefault(handleLogout)}
                    >
                        <i class="nav-icon fas fa-sign-out-alt"></i>
                        <p>{$format("logout")}</p>
                    </a>
                </li>
            </ul>
        </nav>
    </div>
</aside>

<style>
    /* Theme variable overrides */
    .app-sidebar {
        --lte-sidebar-hover-bg: rgba(0, 0, 0, 0.05);
        --lte-sidebar-active-bg: #333333;
        --lte-sidebar-active-color: #333333;
        --lte-sidebar-width: 300px !important;
    }

    .sidebar-wrapper {
        border-right: none !important;
        box-shadow:
            rgba(33, 37, 41, 0.125) 0px 0px 1px 0px,
            rgba(33, 37, 41, 0.2) 0px 4px 3px 0px !important;
    }

    .sidebar-wrapper .sidebar-menu > .nav-item > .nav-link.active,
    .sidebar-wrapper .nav-treeview > .nav-item > .nav-link.active {
        background-color: #333 !important;
    }

    .sidebar-wrapper .nav-icon {
        min-width: 1.25rem !important;
        max-width: 1.25rem !important;
        height: 1.25rem !important;
    }

    /* Chevron arrow smooth rotation */
    .nav-arrow {
        transition: transform 0.25s ease-in-out;
        margin-left: auto;
    }

    :global(.menu-open) > .nav-link .nav-arrow {
        transform: rotate(90deg);
    }

    /* Logo & brand styling */
    .brand-link {
        text-decoration: none !important;
        height: 3.5rem;
    }

    .brand-title {
        font-family: "Montserrat-bold", sans-serif;
        font-size: 1.1rem;
        line-height: 1;
        color: var(--bs-body-color, #333);
    }

    .brand-subtitle {
        font-family: "Montserrat-medium", sans-serif;
        font-size: 0.68rem;
        margin-top: 2px;
        line-height: 1;
        height: 12px;
    }

    .logo-img {
        height: 33px;
        width: auto;
        object-fit: contain;
        margin-left: 5px;
    }

    .pentest-icon {
        width: 1.1rem !important;
        height: auto !important;
        margin-right: 0.5rem !important;
    }
</style>
